import { Icon } from "@mdi/react";
import * as Sentry from "@sentry/react";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import { Add, ArrowBack, ErrorOutlineOutlined } from "@material-ui/icons";
import { mdiClock } from "@mdi/js";
import { Button, Dropdown, InputRef, Popover, Select, Tooltip } from "antd";
import {
  AnchorDragHistory,
  activeTypeWellAnchorEditorAtom,
  draggingTypeWellChartIdAtom,
  pendingDragBAtom,
  pendingDragEURAtom,
  pendingDragPeakAtom,
  pendingDragQiAtom,
  primaryProductEndDateAtom,
  productEURAtom
} from "atoms/arps";
import {
  ArpFields,
  ArpsDesignerKind,
  ProductNames,
  TypeWellInputTableMode
} from "constants/forecasts.constants";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import _debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import { selectSelectedTypeWell, useForecastStore } from "stores/forecast";
import { isDecliningRatio, isPrimaryProduct, isRatio } from "utils/arps";
import { isDefined, isObject, parseNumber } from "utils/fp";

import useBetaFeatures from "hooks/useBetaFeatures";

import { ArpSegment, ForecastConstant, UserArpsItem } from "models/UserArpsModel";

import {
  IArpsUndoManager,
  ProductOperation,
  createFieldCommand,
  createProductCommand
} from "components/arps/hooks/useArpsUndoManager";
import { useSmartQi } from "components/arps/hooks/useSmartQi";
import { useTypeWellTemplates } from "components/arps/hooks/useTypeWellTemplates";
import { ErrorBoundary } from "components/base";
import { CumulativeData } from "components/forecasting/Forecasting";
import { OnTime } from "components/forecasting/models/forecastWell";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { ArpInput, ButtonIconCentered } from "components/shared";
import { RESERVE_CATEGORIES } from "components/sync/util";

import {
  getDefaultRateUnitsFromString,
  productStringToProductTypeEnum
} from "../../../../../utils";
import ChevronDown from "../../../../icons/ChevronDown";
import {
  ForecastArrayT,
  useFetchForecastArray
} from "../../../hooks/useFetchForecastArray";
import useUpdateEUR from "../../../hooks/useUpdateEUR";
import { ProductSegmentTemplate, SegmentTemplate } from "../../../models/SegmentTemplate";
import { planAnchorDragApplication } from "../../../utils/anchorDragEur";
import {
  convertNominalToDeclineType,
  getPrimaryProductLastSegmentEndDate,
  getPrimaryProductLastValidSegmentEndDate,
  getRampUpSegmentCumVolume,
  isConstrainedSegment,
  isRampUpSegment,
  reorderArpsForNewPrimaryProduct
} from "../../../utils/arpsUtils";
import { getDurationInYears } from "../../../utils/dates";
import { getDeclineTitleWithType } from "../../../utils/declineHelpers";
import { groupArpsSegmentsByProduct } from "../../../utils/groupArpsSegmentsByProduct";
import { getHeaderTooltip } from "../../../utils/headerTooltips";
import {
  convertConstantToSegments,
  convertSegmentsToConstant,
  getRatioDisplayValues,
  syncRatioSegmentsWithPrimaryProduct,
  updateRatioDeclineSegments
} from "../../../utils/ratioDeclineUtils";
import {
  combineConsecutiveQfReachedWarnings,
  extendArpsWrapper,
  getAvailableProducts,
  getData,
  getDefaultTypeWellTemplate,
  getOverallTemplateName,
  getPrimaryProduct,
  getSegmentTypeUsingArps,
  getSortedProducts,
  getUniqueRowAndDisplayHeadersSorted,
  isImportedRateTemplate,
  pruneOrphanedDependentConstants,
  toggleOverallTemplateDropdown,
  updateArps,
  updateForecastStartDateToNextProductionMonth,
  updateHeaders,
  updateProductTemplatesAndData,
  validateSegments
} from "../../../utils/typeWellUtils";
import { removeSessionDf } from "../../../utils/updateSegment";
import { arpsWasm } from "../../../utils/wasmLoader";
import {
  cascadeStartDateToAllProducts,
  isFirstProductSegment
} from "../utils/cascadeStartDate";
import { GetProductLabel } from "./GetProductLabel";
import OnTimeEditorModal from "./OnTimeEditorModal";
import { OptionsType, ProductMoreOptions } from "./ProductMoreOptions";
import {
  ColumnContainer,
  ErrorWrapper,
  FlexRightContainer,
  NewProductWrapper,
  ProductColumnContainer,
  ProductColumnWrapper,
  ProductHeaderContainer,
  RootContainer,
  RowHeading,
  SwitchToProductModeWrapper,
  TableContainer,
  TemplateSelectorWrapper,
  WarningWrapper,
  Wrapper
} from "./TypeWellInputTable.styles";
import { TypeWellProductItem, typeWellProductsMenuItems } from "./TypeWellProductOptions";

export interface TypeWellInputTableModel {
  wellKey: string;
  arps?: ICheckedForecast;
  declineType: string;
  selectedTypeWellTitle: string;
  selectedTypeWellRescat: string;
  handleReload?: (item?: UserArpsItem) => void;
  handleReset?: () => void;
  handleSave?: (item?: UserArpsItem) => void;
  onChange: (
    item: ICheckedForecast,
    hasErrors?: boolean,
    hasWarnings?: boolean,
    options?: { recordHistory: boolean }
  ) => void;
  kind: ArpsDesignerKind;
  onOnTimeChange?: (item: OnTime[]) => void;
  saveStatus?;
  cumulativeData?: CumulativeData | null;
  fieldLevelUndoManager?: IArpsUndoManager;
}

export interface ErrorWarning {
  product: string;
  segment?: number;
  message: string;
}

type RestoreDeletedArgs = {
  segments: ArpSegment[];
  constants: ForecastConstant[];
  templateName: string | null;
  calculatedFields: string[];
  arps: ICheckedForecast;
  product: string;
  isConstant?: boolean;
};

export default function TypeWellInputTable({
  wellKey,
  arps,
  declineType,
  selectedTypeWellTitle,
  selectedTypeWellRescat,
  handleReload,
  handleReset,
  handleSave,
  onChange,
  onOnTimeChange,
  kind,
  cumulativeData,
  fieldLevelUndoManager
}: TypeWellInputTableModel) {
  const fsSetSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);

  const { updateEUR } = useUpdateEUR();
  const [, setPrimaryProductEndDate] = useAtom(primaryProductEndDateAtom) as [
    Date | null,
    (value: Date | null) => void
  ];
  const primaryProductEndDate = useAtomValue(primaryProductEndDateAtom);
  const setProductEUR = useSetAtom(productEURAtom);
  const productEUR = useAtomValue(productEURAtom);
  const pendingDragEUR = useAtomValue(pendingDragEURAtom);
  const clearPendingDragEUR = useSetAtom(pendingDragEURAtom);
  const pendingDragQi = useAtomValue(pendingDragQiAtom);
  const clearPendingDragQi = useSetAtom(pendingDragQiAtom);
  const setBEditableSegments = useForecastStore((s) => s.setBEditableSegments);
  const setTypeWellTemplateNames = useForecastStore((s) => s.setTypeWellTemplateNames);
  const pendingDragPeak = useAtomValue(pendingDragPeakAtom);
  const clearPendingDragPeak = useSetAtom(pendingDragPeakAtom);
  const pendingDragB = useAtomValue(pendingDragBAtom);
  const clearPendingDragB = useSetAtom(pendingDragBAtom);
  const setActiveAnchorEditor = useSetAtom(activeTypeWellAnchorEditorAtom);
  const draggingChartId = useAtomValue(draggingTypeWellChartIdAtom);
  const pendingAnchorCommand = useRef<Nullable<() => void>>(null);
  function finalizeAnchorHistory() {
    const commit = pendingAnchorCommand.current;
    pendingAnchorCommand.current = null;
    commit?.();
  }
  useEffect(() => {
    if (kind !== ArpsDesignerKind.TypeWell || !arps?.id) return;
    setActiveAnchorEditor(arps.id);
    return () => {
      finalizeAnchorHistory();
      setActiveAnchorEditor(null);
      clearPendingDragEUR(null);
      clearPendingDragQi(null);
      clearPendingDragPeak(null);
      clearPendingDragB(null);
    };
  }, [
    kind,
    arps?.id,
    setActiveAnchorEditor,
    clearPendingDragEUR,
    clearPendingDragQi,
    clearPendingDragPeak,
    clearPendingDragB
  ]);

  const typeWellSettings = useSelector(
    (state: RootState) => state.userSetting.typeWellSettings
  );

  const { hasFeature } = useBetaFeatures();

  const projectPermissions = useSelectedProjectPermissions();
  const hasForecastLiquidProducts = hasFeature("Forecast Liquid Products");
  const hasForecastSteam = hasFeature("Forecast Steam");

  const typeWellTemplates = useTypeWellTemplates(kind);
  const inputRefs = useRef<MutableRefObject<InputRef>[]>([]);
  const dropdownRef = useRef<HTMLElement | null>(null);

  const { fetchSmartQi } = useSmartQi();

  const [overallSegmentTemplate, setOverallSegmentTemplate] = useState<SegmentTemplate>();
  const [productSegmentTemplate, setProductSegmentTemplate] =
    useState<ProductSegmentTemplate>({});
  const [segmentDetails, setSegmentDetails] = useState<SegmentTemplate>({
    productCols: [],
    name: null,
    productDisplayData: [],
    rowHeaders: [],
    displayHeaders: [],
    type: null,
    numberOfSegments: null,
    update: null,
    initialData: null,
    extendArps: null
  });

  const uniqueProductFields = getUniqueRowAndDisplayHeadersSorted(
    productSegmentTemplate,
    kind
  );

  const [mode, setMode] = useState<TypeWellInputTableMode>(
    TypeWellInputTableMode.Product
  );
  const [errors, setErrors] = useState<ErrorWarning[]>([]);
  const [warnings, setWarnings] = useState<ErrorWarning[]>([]);
  const [templateError, setTemplateError] = useState(undefined);
  const [onTime, setOnTime] = useState<{ open: boolean; product: string }>({
    open: false,
    product: ""
  });

  const [products, setProducts] = useState(
    getAvailableProducts(
      arps,
      typeWellProductsMenuItems(
        kind,
        hasForecastLiquidProducts,
        hasForecastSteam
      ) as TypeWellProductItem[]
    )
  );

  const INITIAL_CALCULATED_FIELDS: { [product: string]: string[] } = {
    [ProductNames.Oil]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.Gas]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.SalesGas]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.Water]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.Steam]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.CondAbbreviatedWithDot]: [ArpFields.EUR, ArpFields.TotalEUR],
    [ProductNames.Condensate]: [ArpFields.EUR, ArpFields.TotalEUR]
  };
  const [calculatedFields, setCalculatedFields] = useState<{
    [product: string]: string[];
  }>(INITIAL_CALCULATED_FIELDS);
  const anchorCalculatedFields = useRef<{
    forecast: AnchorDragHistory["originalForecast"];
    fields: typeof calculatedFields;
  }>();

  const resetCalculatedFields = () => {
    setCalculatedFields(INITIAL_CALCULATED_FIELDS);
  };

  const hasSource = !!arps?.source;

  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const setIsTypeWellSavable = useForecastStore((s) => s.setIsTypeWellSavable);

  const { data }: { data?: ForecastArrayT } = useFetchForecastArray({
    ids: [selectedTypeWell?.id]
  });

  const hasForecastData: boolean =
    data?.forecastArray &&
    selectedTypeWell?.id in data.forecastArray &&
    data.forecastArray[selectedTypeWell.id].length > 0;

  // For the 't' key to toggle the template dropdown
  useHotkeys(
    ["t"],
    (event) => {
      const target = event.target as HTMLElement;

      if (
        event.key !== "t" ||
        ((target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable) &&
          !target.dataset.testid?.startsWith("arp-input-field-"))
      ) {
        return;
      }

      event.preventDefault();

      // Only handle when in Forecasting mode and not in Segment mode
      if (
        kind !== ArpsDesignerKind.Forecasting ||
        mode === TypeWellInputTableMode.Segment
      ) {
        return;
      }

      toggleOverallTemplateDropdown();
    },
    {
      enabled:
        kind === ArpsDesignerKind.Forecasting && mode !== TypeWellInputTableMode.Segment,
      keyup: false,
      enableOnFormTags: ["INPUT"]
    }
  );

  // For the 'c' key to update forecast start date to "current" month (last production month + 1)
  useHotkeys(
    ["c"],
    (event) => {
      const target = event.target as HTMLElement;

      if (
        event.key !== "c" ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey ||
        !arps?.arps ||
        arps.arps.length === 0 ||
        ((target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable) &&
          !target.dataset.testid?.startsWith("arp-input-field-"))
      ) {
        return;
      }

      // Only handle when in Forecasting mode and not in Segment mode
      if (
        kind !== ArpsDesignerKind.Forecasting ||
        mode === TypeWellInputTableMode.Segment ||
        !arps ||
        !cumulativeData ||
        !productSegmentTemplate
      ) {
        return;
      }

      event.preventDefault();

      updateForecastStartDateToNextProductionMonth({
        arps,
        cumulativeData,
        productSegmentTemplate,
        declineType,
        onChange
      });
    },
    {
      enabled:
        kind === ArpsDesignerKind.Forecasting && mode !== TypeWellInputTableMode.Segment,
      keyup: false,
      enableOnFormTags: ["INPUT"]
    }
  );

  const focusPrimaryProductQiField = () => {
    if (inputRefs.current) {
      const primaryProduct: string = getPrimaryProduct({
        kind,
        products: Object.keys(productSegmentTemplate),
        cumulativeData
      });
      const qiFieldRef = inputRefs.current.find(
        (ref) =>
          ref?.current?.input &&
          ref.current.input.name === "Qi" &&
          ref.current.input.getAttribute("data-product") &&
          primaryProduct &&
          ref.current.input.getAttribute("data-product").toLowerCase() ===
            primaryProduct.toLowerCase()
      );
      if (qiFieldRef?.current?.input) {
        qiFieldRef.current.input.focus();
      }
    }
  };

  const prevOverallTemplateName = useRef<string | undefined>();

  useEffect(() => {
    const currentName = overallSegmentTemplate?.name;

    // Only focus Qi if the template name actually changed (not just a re-render)
    if (currentName && currentName !== prevOverallTemplateName.current) {
      prevOverallTemplateName.current = currentName;

      // Delay letting inputRefs update because it takes time for the DOM to update.
      const timeoutId = setTimeout(() => {
        focusPrimaryProductQiField();
      }, 300);
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      prevOverallTemplateName.current = currentName;
    }
  }, [overallSegmentTemplate?.name]);

  // Reset undo stack when switching to a new type well
  useEffect(() => {
    if (fieldLevelUndoManager) {
      fieldLevelUndoManager.reset();
    }
  }, [wellKey, arps?.uniqueID]);

  useEffect(() => {
    if (!arpsWasm || !arps || !arps.arps) {
      return;
    }

    // Determine primary product from productSegmentTemplate (first non-ratio product)
    const primaryProductKey = Object.keys(productSegmentTemplate).find(
      (product) => !isRatio(product)
    );

    // Filter to only non-ratio segments for getData
    const nonRatioSegments = arps.arps.filter((seg) => !isRatio(seg.product));
    // Exclude read-only products so getData's fallback can't mislabel the overall template.
    const readOnlyProducts = new Set(
      Object.entries(getSegmentTypeUsingArps({ arps, typeWellTemplates, kind }))
        .filter(([product, template]) => isImportedRateTemplate(product, template))
        .map(([product]) => product)
    );
    const editableNonRatioSegments = nonRatioSegments.filter(
      (seg) => !readOnlyProducts.has(seg.product)
    );
    const nonRatioArps = {
      ...arps,
      arps:
        editableNonRatioSegments.length > 0 ? editableNonRatioSegments : nonRatioSegments
    };

    setOverallSegmentTemplateAndUpdateHeader(
      getData({
        arps: nonRatioArps,
        arpsLib: arpsWasm,
        segmentTemplateName: overallSegmentTemplate?.name,
        kind,
        setTemplateError,
        typeWellTemplates,
        cumulativeData,
        declineType,
        typeWellSettings,
        updateEUR,
        primaryProduct: primaryProductKey,
        primaryProductEndDate
      })
    );

    // When primaryProductEndDate changes, rebuild product templates to get updated data
    if (isDefined(primaryProductEndDate)) {
      const updatedProductTemplates: ProductSegmentTemplate =
        updateProductTemplatesAndData({
          arps,
          typeWellTemplates,
          arpsWasm,
          kind,
          setTemplateError,
          cumulativeData,
          declineType,
          typeWellSettings,
          updateEUR,
          wellKey,
          primaryProductEndDate
        });
      const sortedProductTemplates: ProductSegmentTemplate = getSortedProducts({
        updatedProductTemplates,
        kind,
        cumulativeData
      });
      setProductSegmentTemplate(sortedProductTemplates);
    }

    setMode(TypeWellInputTableMode.Product);
  }, [
    declineType,
    typeWellTemplates,
    cumulativeData,
    overallSegmentTemplate?.name,
    primaryProductEndDate
  ]);

  // Update productEUR atom whenever productSegmentTemplate changes
  useEffect(() => {
    const eurByProduct: Record<string, number> = {};

    Object.keys(productSegmentTemplate).forEach((product) => {
      const productData = productSegmentTemplate[product]?.productDisplayData?.[0];
      if (isObject(productData) && ArpFields.EUR in productData) {
        const eurValue = productData[ArpFields.EUR];
        const eurNumber = parseNumber(eurValue);
        if (!isNaN(eurNumber)) {
          eurByProduct[product] = eurNumber;
        }
      }
    });

    setProductEUR(eurByProduct);
  }, [productSegmentTemplate, setProductEUR]);

  // Derive, from a product's segment template, which segment index each named
  // b field (B / Btrans / Bhyp) controls. productDisplayData contains one
  // summary per product, not one entry per segment, so use the actual arps
  // segments for indices and rowHeaders for the corresponding field names.
  function computeBFieldMapping(product: string): {
    indices: number[];
    headers: string[];
  } {
    // Bf is deliberately absent: it belongs to the terminal segment, whose
    // anchor lands within a few pixels of the EUR anchor and makes EUR hard to
    // grab. Bf stays editable through the input table.
    const template = productSegmentTemplate[product];
    const B_FIELDS = ["B", "Btrans", "Bhyp"];
    const headers: string[] = Array.isArray(template?.rowHeaders)
      ? template.rowHeaders
      : [];
    const bHeaders = headers.filter((h) => B_FIELDS.includes(h));
    const segments = arps?.arps?.filter((segment) => segment.product === product) ?? [];
    if (bHeaders.length === 0 || segments.length === 0) {
      return { indices: [], headers: [] };
    }
    // Candidates are the segments that can carry a b at all: ramp segments
    // hold b = -1 and never can.
    let candidates = segments
      .map((segment, index) => ({ segment, index }))
      .filter(({ segment }) => segment.b >= 0)
      .map(({ index }) => index);
    // Bcons belongs to the constrained first segment, which is not draggable.
    if (headers.includes("Bcons")) candidates = candidates.slice(1);
    // The named b fields map onto those candidates in order.
    return { indices: candidates.slice(0, bHeaders.length), headers: bHeaders };
  }

  // Which header (B / Btrans / Bhyp) a dragged b anchor's segIndex maps to, so
  // the write goes through updateSegment's per-field logic (correct segment,
  // Secant di/df conversion) instead of a bare inline write. Returns null
  // when segIndex isn't in the published mapping (stale or not-yet-published
  // template) -- the caller should bail rather than guess a header, since
  // defaulting to ArpFields.B would silently write the wrong segment.
  function bHeaderForSegment(productName: string, segIndex: number): string | null {
    const { indices, headers } = computeBFieldMapping(productName);
    const ordinal = indices.indexOf(segIndex);
    return ordinal >= 0 ? headers[ordinal] : null;
  }

  // Publish which segments expose an editable b, and the resolved template
  // name per product, so the chart can draw the right anchors without
  // inferring the template from segment shape.
  //
  // Keyed by `${typeWellId}:${product}`, not product alone: these maps are
  // overwritten wholesale on every publish, so switching to a type well whose
  // template has not resolved yet (or that has no entry for a given product)
  // would otherwise still read the *previously selected* type well's answer
  // for that key -- silently, since a stale hit looks identical to a fresh
  // one. That is what lets a real SAGD well fall through the chart's SAGD
  // exclusion gate when the prior selection was a non-SAGD template using the
  // same product name.
  useEffect(() => {
    if (!arps?.id) return;
    const byProduct: Record<string, number[]> = {};
    Object.keys(productSegmentTemplate ?? {}).forEach((product) => {
      byProduct[`${arps.id}:${product}`] = computeBFieldMapping(product).indices;
    });
    if (!isEqual(useForecastStore.getState().bEditableSegments, byProduct)) {
      setBEditableSegments(byProduct);
    }

    const namesByProduct: Record<string, string> = {};
    Object.keys(productSegmentTemplate ?? {}).forEach((product) => {
      const name = productSegmentTemplate[product]?.name;
      if (typeof name === "string") namesByProduct[`${arps.id}:${product}`] = name;
    });
    if (!isEqual(useForecastStore.getState().typeWellTemplateNames, namesByProduct)) {
      setTypeWellTemplateNames(namesByProduct);
    }
  }, [arps, productSegmentTemplate, setBEditableSegments, setTypeWellTemplateNames]);

  // Use the typed-field solver for anchors and reject writes after selection
  // or permission changes. The mounted editor registration gates the chart.
  // Carry the mouse-down snapshot through preview and commit so the final
  // mouseup creates one undo entry for all the gesture's coupled field changes.
  useEffect(() => {
    if (!pendingDragEUR) return;
    clearPendingDragEUR(null);
    if (!arps) return;
    if (pendingDragEUR.typeWellId !== arps.id || !projectPermissions.canEditTypeWells)
      return;
    onArpsValueChange(
      arps,
      pendingDragEUR.productName,
      ArpFields.EUR,
      pendingDragEUR.eur,
      pendingDragEUR
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDragEUR]);

  useEffect(() => {
    if (!pendingDragQi) return;
    clearPendingDragQi(null);
    if (!arps) return;
    if (pendingDragQi.typeWellId !== arps.id || !projectPermissions.canEditTypeWells)
      return;

    // On a ramp-up template the anchor sits at the ramp's starting rate, which
    // the editor exposes as Q0 -- not Qi (the peak). Q0 cannot be applied by
    // writing seg.qi inline: updateSegment also runs updateRampUpSegmentValues
    // to recompute the harmonic (b = -1) ramp, and skipping that leaves di/df
    // inconsistent and the arps unsolvable. So route it through updateSegment,
    // via the canonical isRampUpSegment/isConstrainedSegment helpers (which
    // also fall back to the resolved template name when the bare structural
    // test is ambiguous) rather than reimplementing their check inline.
    const productSegments = arps.arps.filter(
      (seg) => seg.product === pendingDragQi.productName
    );
    if (productSegments[0]?.isUserRatioDecline === true) {
      // The first ratio anchor edits Q0. Its junction anchor uses the Qi path
      // below, which updates both the first segment's Qf and the plateau's Qi.
      // Exactly two segments is a precondition of updateRatioDeclineSegments,
      // enforced upstream by useChartTypeWellDrag's isProductEditable gate --
      // the sole writer of this atom. A malformed forecast reaching here throws
      // and surfaces as a field error rather than writing the wrong field.
      onArpsValueChange(
        arps,
        pendingDragQi.productName,
        ArpFields.Q0,
        pendingDragQi.qi,
        pendingDragQi
      );
      return;
    }
    const segmentTemplateName =
      productSegmentTemplate[pendingDragQi.productName]?.name ?? "";
    const isRampUpTemplate = isRampUpSegment({
      segmentTemplateName,
      segments: productSegments
    });
    const isConstrainedTemplate = isConstrainedSegment({
      segmentTemplateName,
      segments: productSegments
    });
    if (isRampUpTemplate || isConstrainedTemplate) {
      if (
        pendingDragQi.eur != null &&
        applyFieldPreservingEUR(
          arps,
          pendingDragQi.productName,
          ArpFields.Q0,
          pendingDragQi.qi,
          pendingDragQi.eur,
          pendingDragQi
        )
      ) {
        return;
      }
      // No EUR anchor available: apply Q0 alone and let EUR float.
      onArpsValueChange(
        arps,
        pendingDragQi.productName,
        ArpFields.Q0,
        pendingDragQi.qi,
        pendingDragQi
      );
      return;
    }

    applyQiPreservingEur(
      arps,
      pendingDragQi.productName,
      pendingDragQi.qi,
      pendingDragQi.eur,
      pendingDragQi
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDragQi]);

  useEffect(() => {
    if (!pendingDragPeak) return;
    clearPendingDragPeak(null);
    if (!arps) return;
    if (pendingDragPeak.typeWellId !== arps.id || !projectPermissions.canEditTypeWells)
      return;
    applyQiPreservingEur(
      arps,
      pendingDragPeak.productName,
      pendingDragPeak.qi,
      pendingDragPeak.eur,
      pendingDragPeak
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDragPeak]);

  useEffect(() => {
    if (!pendingDragB) return;
    clearPendingDragB(null);
    if (!arps) return;
    if (pendingDragB.typeWellId !== arps.id || !projectPermissions.canEditTypeWells)
      return;

    // Route through applyFieldPreservingEUR the same way Q0/Qi do -- a bare
    // inline `{ ...seg, b }` write skips updateSegment's Secant di/df
    // conversion, and writing ArpFields.B regardless of which segment this is
    // (Btrans vs Bhyp) edits the wrong segment on a 3-segment template.
    const header = bHeaderForSegment(pendingDragB.productName, pendingDragB.segIndex);
    // No published mapping for this segIndex (stale or not-yet-published
    // template) -- bail rather than guess a header and risk writing the
    // wrong segment.
    if (header === null) return;
    if (
      pendingDragB.eur != null &&
      applyFieldPreservingEUR(
        arps,
        pendingDragB.productName,
        header,
        pendingDragB.b,
        pendingDragB.eur,
        pendingDragB
      )
    ) {
      return;
    }
    // Fallback: no EUR anchor, or the EUR solve cannot run on this segment
    // shape (planAnchorDragApplication) -- apply b alone, EUR floats.
    onArpsValueChange(
      arps,
      pendingDragB.productName,
      header,
      pendingDragB.b,
      pendingDragB
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDragB]);

  useEffect(() => {
    // Chart teardown (normalization, lasso, permissions) may end a gesture
    // without mouseup. Keep its last applied preview as one undoable edit.
    if (draggingChartId === null) finalizeAnchorHistory();
  }, [draggingChartId]);

  useEffect(() => {
    if (!arpsWasm || !arps || !arps.arps) {
      return;
    }

    if (!arps.arps.length) {
      const defaultTypeWellTemplate = getDefaultTypeWellTemplate({
        kind,
        typeWellTemplates
      });
      setOverallSegmentTemplate(defaultTypeWellTemplate);
    }

    setProducts(
      getAvailableProducts(
        arps,
        typeWellProductsMenuItems(
          kind,
          hasForecastLiquidProducts,
          hasForecastSteam
        ) as TypeWellProductItem[]
      )
    );
    setMode(TypeWellInputTableMode.Product);

    const updatedProductTemplates: ProductSegmentTemplate = updateProductTemplatesAndData(
      {
        arps,
        typeWellTemplates,
        arpsWasm,
        kind,
        setTemplateError,
        cumulativeData,
        declineType,
        typeWellSettings,
        updateEUR,
        wellKey
      }
    );
    const sortedProductTemplates: ProductSegmentTemplate = getSortedProducts({
      updatedProductTemplates,
      kind,
      cumulativeData
    });

    if (sortedProductTemplates && Object.keys(sortedProductTemplates).length > 0) {
      const { hasErrors, hasWarnings } = validateSegmentsAndUpdateStatesWrapper({
        arps: arps.arps,
        productSegmentTemplate: sortedProductTemplates
      });
      if (kind === ArpsDesignerKind.Forecasting) {
        onChange && onChange(arps, hasErrors, hasWarnings);
      }
    }

    setProductSegmentTemplate(sortedProductTemplates);

    const overallName = getOverallTemplateName({
      overallSegmentTemplate,
      productSegmentTemplate: sortedProductTemplates
    });

    const allSameTemplate: boolean = overallName !== "Mixed Templates";
    if (allSameTemplate) {
      // Get first NON-RATIO template (matching the logic in getOverallTemplateName)
      const firstNonRatioTemplate = Object.values(sortedProductTemplates).find(
        (template) =>
          template.productCols.length > 0 &&
          !isRatio(template.productCols[0]) &&
          !isImportedRateTemplate(template.productCols[0], template)
      );
      if (firstNonRatioTemplate) {
        setOverallSegmentTemplateAndUpdateHeader(firstNonRatioTemplate);
      }
    }
  }, [arps, setProducts, typeWellTemplates, cumulativeData]);

  const debounceUpdate = useMemo(
    () =>
      _debounce(
        (arps: ICheckedForecast, product: string, header: string, val, fn) =>
          fn(arps, product, header, val),
        600
      ),
    []
  );

  const setOverallSegmentTemplateAndUpdateHeader = (newTemplate: SegmentTemplate) => {
    if (!newTemplate) return;

    const updatedTemplate = updateHeaders(newTemplate, declineType);
    setOverallSegmentTemplate(updatedTemplate);
  };

  const validateSegmentsAndUpdateStatesWrapper = ({
    arps,
    productSegmentTemplate
  }: {
    arps: ArpSegment[];
    productSegmentTemplate: ProductSegmentTemplate;
  }) => {
    const allErrors = [];
    const allWarnings = [];

    const firstProduct = Object.keys(productSegmentTemplate)[0];

    // First pass: collect all errors and warnings to identify problematic segments
    const primaryProductSegmentsWithIssues = new Set<number>();

    Object.keys(productSegmentTemplate).forEach((product) => {
      const isPrimaryTypeWellSegment: boolean = firstProduct === product;

      const arpsFiltered = arps.filter(
        (segment: ArpSegment): boolean => segment?.product === product
      );

      // Use a temporary primaryProductEndDate for initial validation
      const tempPrimaryProductEndDate: Date = getPrimaryProductLastSegmentEndDate({
        segments: arps,
        kind,
        cumulativeData
      });

      // Skip validation for ratio decline segments: user-built simple declines are
      // manually constructed, and imported ratio Arps (EVA-6256) are read-only. Neither
      // are standard editable segments, and validateSegments assumes a primary product.
      const isRatioDeclineSegments =
        arpsFiltered.length > 0 &&
        (arpsFiltered[0].isUserRatioDecline === true ||
          isDecliningRatio(arpsFiltered[0].product));

      if (isRatioDeclineSegments) {
        return;
      }

      // Read-only rate columns aren't standard editable segments either.
      if (isImportedRateTemplate(product, productSegmentTemplate[product])) {
        return;
      }

      const { errors, warnings } = validateSegments({
        arpsSegments: arpsFiltered,
        productSegmentTemplate,
        kind,
        cumulativeData,
        primaryProductEndDate: tempPrimaryProductEndDate,
        isPrimaryTypeWellSegment
      });

      if (errors.length > 0) {
        allErrors.push(errors);
        // Collect segment numbers with errors for primary product
        if (isPrimaryTypeWellSegment) {
          errors.forEach((error) => {
            if (error.segment) {
              primaryProductSegmentsWithIssues.add(error.segment);
            }
          });
        }
      }
      if (warnings.length > 0) {
        allWarnings.push(warnings);
        // Collect segment numbers with warnings for primary product
        // added for "Not required" warnings
        if (isPrimaryTypeWellSegment) {
          warnings.forEach((warning) => {
            if (warning.segment && warning.message.includes("Not required")) {
              primaryProductSegmentsWithIssues.add(warning.segment);
            }
          });
        }
      }
    });

    // Second pass: calculate the correct primaryProductEndDate excluding segments with issues
    const primaryProductEndDate: Date = getPrimaryProductLastValidSegmentEndDate({
      segments: arps,
      kind,
      cumulativeData,
      segmentsWithIssues:
        primaryProductSegmentsWithIssues.size > 0
          ? primaryProductSegmentsWithIssues
          : undefined
    });
    setPrimaryProductEndDate(primaryProductEndDate || null);

    const flatErrors = allErrors.flat();
    const flatWarnings = allWarnings.flat();

    setErrors(flatErrors);
    setWarnings(flatWarnings);

    // forecasters do not want to see the warning about end date being 6+ months earlier in the hierarchy
    const filteredWarnings = flatWarnings.filter(
      (warning) => !warning.message.includes("end date is 6+ months earlier")
    );

    return {
      hasErrors: flatErrors.length > 0,
      hasWarnings: filteredWarnings.length > 0
    };
  };

  // Helper to restore deleted product or constant (used in undo callbacks)
  const restoreDeletedProductOrConstant = (args: RestoreDeletedArgs) => {
    const { segments, constants, templateName, arps, product, isConstant = false } = args;
    const currentForecast = arps;
    if (!currentForecast) return;

    // Remove any existing segments/constants for this product
    const existingSegmentsWithoutThisProduct = currentForecast.arps.filter(
      (segment) => segment.product !== product
    );
    const existingConstantsWithoutThisProduct = (currentForecast.constants || []).filter(
      (constant) => constant.product.toUpperCase() !== product.toUpperCase()
    );

    const restoredForecast = {
      ...currentForecast,
      arps: isConstant
        ? [...currentForecast.arps]
        : [...existingSegmentsWithoutThisProduct, ...segments],
      constants: [...existingConstantsWithoutThisProduct, ...constants]
    };

    // Restore calculated fields
    setCalculatedFields((prev) => ({
      ...prev,
      [product]: args.calculatedFields || [ArpFields.EUR, ArpFields.TotalEUR]
    }));

    // Only remove undo commands for the restored product, keep others
    if (fieldLevelUndoManager) {
      fieldLevelUndoManager.removeCommandsForProduct(product);
    }

    // Rebuild templates properly based on the restored segment/constant data
    const updatedProductTemplates: ProductSegmentTemplate = updateProductTemplatesAndData(
      {
        arps: restoredForecast,
        typeWellTemplates,
        arpsWasm,
        kind,
        setTemplateError,
        cumulativeData,
        declineType,
        typeWellSettings,
        updateEUR,
        wellKey
      }
    );

    // Ensure the restored product uses the original template name (if not a constant)
    if (!isConstant && updatedProductTemplates[product] && templateName) {
      updatedProductTemplates[product].name = templateName;
    }

    const sortedProductTemplates: ProductSegmentTemplate = getSortedProducts({
      updatedProductTemplates,
      kind,
      cumulativeData
    });

    if (sortedProductTemplates && Object.keys(sortedProductTemplates).length > 0) {
      const { hasErrors, hasWarnings } = validateSegmentsAndUpdateStatesWrapper({
        arps: restoredForecast.arps,
        productSegmentTemplate: sortedProductTemplates
      });
      if (kind === ArpsDesignerKind.Forecasting) {
        onChange && onChange(restoredForecast, hasErrors, hasWarnings);
      }
      onChange && onChange(restoredForecast);
    }

    setProductSegmentTemplate(sortedProductTemplates);
  };

  const deleteProduct = (product: string) => {
    // Filter by identity: a product's segments are not always contiguous (e.g. imports).
    const removeProductFromList = (list) =>
      list.filter((item) => item.product !== product);

    // Capture current state for undo functionality
    const currentProductSegments = arps.arps.filter(
      (segment) => segment.product === product
    );
    const currentProductConstants = (arps.constants || []).filter(
      (constant) => constant.product.toUpperCase() === product.toUpperCase()
    );
    // Only capture the template name, not the entire template with display data
    const currentProductTemplateName = productSegmentTemplate[product]?.name;
    const currentCalculatedFields = calculatedFields[product];

    const updatedProductSegmentTemplate = { ...productSegmentTemplate };
    const arpsDecline = removeProductFromList(arps.arps);

    // If there is no primary prod in the arps, remove all constants.
    // Otherwise, also prune dependent constants (C2-C5, O+W) whose engine
    // inputs this deletion removed — the menu gates their entry, this is the
    // exit path (EVA-121 QA round 2). Pruned constants are bundled into the
    // undo capture below so undoing the delete restores them too.
    const { kept: updatedForecastConstants, pruned: prunedConstants } = arpsDecline.length
      ? pruneOrphanedDependentConstants(
          arpsDecline,
          removeProductFromList(arps.constants)
        )
      : { kept: [], pruned: [] };

    const userArps = Object.assign({}, arps, {
      arps: arpsDecline,
      constants: updatedForecastConstants
    });

    delete updatedProductSegmentTemplate[product];
    if (updatedForecastConstants.length === 0) {
      for (const constant of arps.constants) {
        delete updatedProductSegmentTemplate[constant.product];
      }
    }
    setProductSegmentTemplate(updatedProductSegmentTemplate);

    setCalculatedFields({
      ...calculatedFields,
      [product]: [ArpFields.EUR, ArpFields.TotalEUR]
    });
    onChange && onChange(userArps);
    removeSessionDf({
      key: wellKey,
      product
    });

    // Add product or constant deletion to undo stack
    if (fieldLevelUndoManager) {
      // First, remove any existing field change commands for this product
      fieldLevelUndoManager.removeCommandsForProduct(product);

      // If deleting a product segment, create a ProductCommand for it
      if (currentProductSegments.length > 0) {
        const productDeletionCommand = createProductCommand(
          product,
          ProductOperation.DELETE,
          {
            segments: currentProductSegments,
            constants: [...currentProductConstants, ...prunedConstants],
            template: currentProductTemplateName, // Store just the template name
            calculatedFields: currentCalculatedFields
          },
          () => {
            // Execute: deletion already happened above
          },
          () => {
            restoreDeletedProductOrConstant({
              segments: currentProductSegments,
              constants: [...currentProductConstants, ...prunedConstants],
              templateName: currentProductTemplateName,
              calculatedFields: currentCalculatedFields,
              arps,
              product,
              isConstant: false
            });
          }
        );
        fieldLevelUndoManager.addCommand(productDeletionCommand);
      }

      // If deleting a constant (and not a product segment), create a ProductCommand for it
      else if (currentProductConstants.length > 0) {
        const constantDeletionCommand = createProductCommand(
          product,
          ProductOperation.DELETE,
          {
            segments: [],
            constants: [...currentProductConstants, ...prunedConstants],
            template: null,
            calculatedFields: currentCalculatedFields
          },
          () => {
            // Execute: deletion already happened above
          },
          () => {
            restoreDeletedProductOrConstant({
              segments: [],
              constants: [...currentProductConstants, ...prunedConstants],
              templateName: null,
              calculatedFields: currentCalculatedFields,
              arps,
              product,
              isConstant: true
            });
          }
        );
        fieldLevelUndoManager.addCommand(constantDeletionCommand);
      }
    }
  };

  const switchToSegmentMode = (product: string) => {
    const segmentTemplate = productSegmentTemplate[product];
    const idx = segmentTemplate.productCols.indexOf(product);
    if (idx < 0) {
      return;
    }
    const productArps: ArpSegment[] = arps.arps.filter(
      (segment) => segment.product === product
    );
    const isRampUp = isRampUpSegment({
      segmentTemplateName: segmentTemplate.name,
      segments: productArps
    });

    const totalProductEUR = productEUR[product] || 0;
    const isLastSegment = (index: number) => index === productArps.length - 1;

    const clone: SegmentTemplate = {
      productCols: productArps.map((item) => item.product),
      name: segmentTemplate.name,
      productDisplayData: productArps.map((item, i) => {
        const vol =
          isRampUp && i == 0
            ? getRampUpSegmentCumVolume(item)
            : arpsWasm.getSegmentVolume(
                item.qi,
                item.di,
                item.b,
                item.qf,
                BigInt(Math.floor(new Date(item.startDate).getTime() / 1000)),
                BigInt(Math.floor(new Date(item.endDate).getTime() / 1000))
              );

        const segmentData = {
          "Start Date": item.startDate,
          Qi: item.qi,
          maxRate: item.maxRate,
          [getDeclineTitleWithType("Di", declineType)]: convertNominalToDeclineType({
            nominalDeclineValueDecimal: item.di,
            bValue: item.b,
            type: declineType
          }),
          B: item.b,
          [getDeclineTitleWithType("Df", declineType)]: convertNominalToDeclineType({
            nominalDeclineValueDecimal: item.df,
            bValue: item.b,
            type: declineType
          }),
          Qf: item.qf,
          "End Date": item.endDate,
          Duration: getDurationInYears(item.startDate, item.endDate),
          Volume: (vol * 0.001).toFixed(2)
        };

        // Only add EUR to the last segment
        if (isLastSegment(i)) {
          segmentData["EUR"] = totalProductEUR.toFixed(2);
        }

        return segmentData;
      }),
      rowHeaders: [
        "Start Date",
        "Duration",
        "Qi",
        "maxRate",
        getDeclineTitleWithType("Di", declineType),
        "B",
        getDeclineTitleWithType("Df", declineType),
        "Qf",
        "End Date",
        "Volume",
        "EUR"
      ],
      displayHeaders: [
        "Start Date",
        "Duration",
        "Q.i",
        "Max Rate",
        "D.i",
        "B",
        "D.f",
        "Q.f",
        "End Date",
        "Volume",
        "EUR"
      ]
    } as SegmentTemplate;

    setSegmentDetails(clone);
    setMode(TypeWellInputTableMode.Segment);
  };

  const onAddNewProduct = async (item) => {
    if (!arps || !overallSegmentTemplate) {
      return;
    }

    // Fetch Smart Qi for the new product FIRST, before creating segments
    const newProduct = item.key;
    const productKey = newProduct.toUpperCase();
    let productName: ProductNames | undefined;

    if (productKey === ProductNames.Oil) {
      productName = ProductNames.Oil;
    } else if (productKey === ProductNames.Gas) {
      productName = ProductNames.Gas;
    } else if (productKey === ProductNames.Water) {
      productName = ProductNames.Water;
    } else if (
      productKey === ProductNames.Condensate ||
      productKey === ProductNames.CondAbbreviated ||
      productKey === ProductNames.CondAbbreviatedWithDot
    ) {
      productName = ProductNames.Condensate;
    } else if (productKey === ProductNames.SalesGas) {
      productName = ProductNames.SalesGas;
    } else if (productKey === ProductNames.Steam) {
      productName = ProductNames.Steam;
    }

    // Fetch Smart Qi and wait for it to complete
    let fetchedSmartQiValue: number | null = null;
    if (productName) {
      fetchedSmartQiValue = await fetchSmartQi(productName);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    const arpsClone = {
      ...arps,
      title: selectedTypeWellTitle,
      reserveCategory: selectedTypeWellRescat
    };
    arpsClone.arps = arpsClone.arps ?? [];
    arpsClone.constants = arpsClone.constants ?? [];

    // Constants do not have a template, so need to filter them out. Read-only too: initialData is null.
    const previousProductTemplate = Object.values(productSegmentTemplate)
      .filter(
        (template) =>
          !isRatio(template.productCols[0]) &&
          !isImportedRateTemplate(template.productCols[0], template)
      )
      .pop();
    const template: SegmentTemplate =
      productSegmentTemplate[item.key] ||
      previousProductTemplate ||
      overallSegmentTemplate;

    const primaryProductSegment: ArpSegment[] = arpsClone.arps.filter((item) =>
      isPrimaryProduct(item.product)
    );

    const primaryProducts = Array.from(
      new Set(primaryProductSegment.map((item) => item.product.toUpperCase()))
    );
    if (primaryProducts.findIndex((p) => p.toUpperCase() === item.key) >= 0) {
      return;
    }

    // newProduct already declared at top of function
    // Only create segments for primary products, not for ratio products
    let newSegments = [];

    if (isPrimaryProduct(newProduct)) {
      newSegments = template.initialData({
        product: newProduct,
        startDate: primaryProductSegment[0]?.startDate
      });

      // If Smart Qi was fetched, apply it to segment 1 and recalculate all segments
      if (fetchedSmartQiValue !== null && fetchedSmartQiValue > 0) {
        const roundedValue = Math.round(fetchedSmartQiValue * 100) / 100;

        const isRampUp = isRampUpSegment({
          segmentTemplateName: template.name,
          segments: newSegments
        });

        const isConstrained = isConstrainedSegment({
          segmentTemplateName: template.name,
          segments: newSegments
        });

        // For constrained templates, Q0 should equal Qi so the initial segment is flat
        // For other templates, preserve existing q0 or default to 0
        const q0Value = isConstrained
          ? roundedValue
          : newSegments[0]?.q0 !== undefined
          ? newSegments[0].q0
          : 0;

        // Update segment 1 with Smart Qi
        newSegments = newSegments.map((segment, index) => {
          if (index === 0) {
            if (isRampUp) {
              return segment;
            }
            // For constrained segments, also set di to 0 since Q0 = Qi (flat)
            if (isConstrained) {
              return {
                ...segment,
                qi: roundedValue,
                qf: roundedValue,
                q0: q0Value,
                di: 0
              };
            }
            return { ...segment, qi: roundedValue, q0: q0Value };
          }
          return segment;
        });

        // Recalculate all segments based on the new Qi value
        try {
          newSegments = updateArps({
            template,
            productArps: newSegments,
            header: "Qi",
            val: roundedValue,
            typeWellSettings,
            declineType,
            cumulativeData,
            setCalculatedFields,
            calculatedFields,
            primaryProduct: Object.keys(productSegmentTemplate)[0] || newProduct
          });
        } catch (err) {
          Sentry.withScope((scope) => {
            scope.setContext("smartQi", {
              product: newProduct,
              smartQiValue: roundedValue,
              templateName: template?.name,
              segmentCount: newSegments.length
            });
            scope.setLevel("error");
            Sentry.captureException(err);
          });
        }
      }
    }

    const newList = isPrimaryProduct(newProduct)
      ? [...arpsClone.arps].concat(newSegments)
      : [...arpsClone.arps];
    // ensure not null
    const constants = isRatio(newProduct)
      ? [...arpsClone.constants].concat([
          {
            product: newProduct,
            value: 0,
            unit: getDefaultRateUnitsFromString(newProduct),
            uniqueId: arpsClone.uniqueID
          }
        ])
      : [...arpsClone.constants];
    const userArps = {
      ...arpsClone,
      arps: newList,
      constants
    };

    // Create the segments and constants that are being added for this product
    // Use the same recalculated segments that were added to newList
    const addedSegments = isPrimaryProduct(newProduct) ? newSegments : [];
    const addedConstants = isRatio(newProduct)
      ? [
          {
            product: newProduct,
            value: 0,
            unit: getDefaultRateUnitsFromString(newProduct),
            uniqueId: arpsClone.uniqueID
          }
        ]
      : [];

    setCalculatedFields({
      ...calculatedFields,
      [newProduct.label]: [ArpFields.EUR, ArpFields.TotalEUR]
    });
    onChange && onChange(userArps);

    // Add product addition to undo stack
    if (fieldLevelUndoManager) {
      const productAdditionCommand = createProductCommand(
        newProduct,
        ProductOperation.ADD,
        {
          segments: addedSegments,
          constants: addedConstants,
          template: template?.name || null,
          calculatedFields: [ArpFields.EUR, ArpFields.TotalEUR]
        },
        () => {
          // Execute: addition already happened above
        },
        () => {
          // Undo: remove the product that was just added (without creating another undo command)
          const removeProductFromList = (list) => {
            const idx = list.findIndex((item) => item.product == newProduct);
            if (idx < 0) {
              return list;
            }
            const len = list.filter((item) => item.product === newProduct).length;
            const newList = [...list];
            newList.splice(idx, len);
            return newList;
          };

          const updatedProductSegmentTemplate = { ...productSegmentTemplate };
          const arpsDecline = removeProductFromList(arps.arps);
          const updatedForecastConstants = removeProductFromList(arps.constants || []);

          const undoUserArps = Object.assign({}, arps, {
            arps: arpsDecline,
            constants: updatedForecastConstants
          });

          delete updatedProductSegmentTemplate[newProduct];
          setProductSegmentTemplate(updatedProductSegmentTemplate);
          setCalculatedFields((prev) => {
            const updated = { ...prev };
            delete updated[newProduct];
            return updated;
          });
          onChange && onChange(undoUserArps);
        }
      );
      fieldLevelUndoManager.executeProductCommand(productAdditionCommand);
    }
  };

  const switchToProductMode = () => {
    if (!arpsWasm) {
      return;
    }
    setMode(TypeWellInputTableMode.Product);
  };

  const onChangePrimaryProduct = (product: string) => {
    if (!arps) {
      return;
    }

    const reorderedArps = reorderArpsForNewPrimaryProduct(arps, product);

    // Trigger full validation and state updates
    validateSegmentsAndUpdateStatesWrapper({
      arps: reorderedArps.arps,
      productSegmentTemplate
    });

    onChange(reorderedArps);
  };

  function getProductInputs({
    product,
    refs,
    productIndex,
    segmentIndex,
    forecastConstant,
    isRatio,
    isSync
  }: {
    product: string;
    refs: MutableRefObject<MutableRefObject<InputRef>[]>;
    productIndex: number;
    segmentIndex: number;
    forecastConstant: boolean;
    isRatio: boolean;
    isSync: boolean;
  }) {
    const uniqueFieldsSet = getUniqueRowAndDisplayHeadersSorted(
      productSegmentTemplate,
      kind
    );
    // EVA-6256: imported ratio Arps (declining ratio stored as unflagged segments, not a
    // constant) are shown read-only — the user didn't build them with our simple template.
    const isImportedRatioArps =
      isRatio &&
      isDecliningRatio(product) &&
      !forecastConstant &&
      (arps?.arps ?? []).some((seg) => seg.product === product) &&
      !(arps?.arps ?? []).some(
        (seg) => seg.product === product && seg.isUserRatioDecline === true
      );
    const segmentNumber = segmentIndex + 1;
    const hasError = errors.some(
      (error) =>
        error.product === product &&
        (mode === TypeWellInputTableMode.Segment
          ? error.segment == undefined || error.segment === segmentNumber
          : true)
    );
    const hasWarning = warnings.some(
      (warning) =>
        warning.product === product &&
        (mode === TypeWellInputTableMode.Segment
          ? warning.segment == undefined || warning.segment === segmentNumber
          : true)
    );

    return (
      <ProductColumnWrapper key={product + "" + segmentIndex}>
        <ProductHeaderContainer>
          {GetProductLabel({
            mode,
            cumulativeData,
            product,
            kind,
            productIndex,
            segmentIndex,
            onChangePrimaryProduct
          })}
          {hasError ? (
            <Popover
              content={
                <div>
                  {errors
                    .filter(
                      (error) =>
                        error.product === product &&
                        (mode === TypeWellInputTableMode.Segment
                          ? error.segment == undefined || error.segment === segmentNumber
                          : true)
                    )
                    .map((error: ErrorWarning, index: number) => (
                      <div key={index}>{error.message}</div>
                    ))}
                </div>
              }>
              <ErrorWrapper data-testid={"typewell-input-table-error-" + product}>
                <ErrorOutlineOutlined />
              </ErrorWrapper>
            </Popover>
          ) : null}
          {hasWarning ? (
            <Popover
              content={
                <div data-testid={"typewell-input-table-warning-popover-" + product}>
                  {(() => {
                    // Filter warnings for this product and segment (if in Segment mode)
                    const filteredWarnings = warnings.filter(
                      (warning) =>
                        warning.product === product &&
                        (mode === TypeWellInputTableMode.Segment
                          ? warning.segment == undefined ||
                            warning.segment === segmentNumber
                          : true)
                    );

                    // In Product mode, combine consecutive "Qf already reached" warnings
                    const displayWarnings =
                      mode === TypeWellInputTableMode.Product
                        ? combineConsecutiveQfReachedWarnings(filteredWarnings)
                        : filteredWarnings;

                    return displayWarnings.map((warning: ErrorWarning, index: number) => (
                      <div key={index}>{warning.message}</div>
                    ));
                  })()}
                </div>
              }>
              <WarningWrapper
                data-testid={"typewell-input-table-warning-wrapper-" + product}>
                <ErrorOutlineOutlined />
              </WarningWrapper>
            </Popover>
          ) : null}
          {mode !== TypeWellInputTableMode.Segment && !isSync && (
            <FlexRightContainer>
              {ProductMoreOptions({
                product,
                isSync,
                switchToSegmentMode,
                setOnTime,
                deleteProduct,
                kind,
                projectPermissions
              }).map((item: OptionsType) => (
                <span
                  key={item.key}
                  onClick={!item.disabled ? item.onClick : undefined}
                  style={{
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    color: item.danger ? "red" : "inherit"
                  }}>
                  {item.label}
                </span>
              ))}
            </FlexRightContainer>
          )}
        </ProductHeaderContainer>
        <ColumnContainer>
          {(mode === TypeWellInputTableMode.Segment
            ? segmentDetails.rowHeaders
            : uniqueFieldsSet.rowHeaders
          ).map((header: string) => {
            const segmentData =
              mode === TypeWellInputTableMode.Segment
                ? segmentDetails?.productDisplayData[segmentIndex]
                : productSegmentTemplate[product]?.productDisplayData[segmentIndex];

            const doesFieldExist =
              mode === TypeWellInputTableMode.Segment
                ? segmentDetails?.rowHeaders.includes(header) &&
                  segmentData != null &&
                  header in (segmentData as GenericObject)
                : productSegmentTemplate[product]?.rowHeaders.includes(header) &&
                  segmentData != null &&
                  header in (segmentData as GenericObject);

            return (
              <ArpInput
                key={header + "-" + productStringToProductTypeEnum(product)}
                header={header}
                product={productStringToProductTypeEnum(product)}
                mode={mode}
                segmentData={segmentData}
                isCalculated={calculatedFields[product]?.indexOf(header) >= 0}
                isForecastConstant={forecastConstant}
                isRatio={isRatio}
                isSync={isSync}
                handleReload={handleReload}
                handleReset={handleReset}
                handleSave={handleSave}
                onChange={(val) => {
                  header === "Template"
                    ? onSelectedProductTemplateChanged({ product, templateName: val })
                    : header === ArpFields.EUR
                    ? debounceUpdate(arps, product, header, val, onArpsValueChange)
                    : onArpsValueChange(arps, product, header, val);
                }}
                typeWellTemplates={typeWellTemplates}
                productSegmentTemplateName={productSegmentTemplate[product]?.name}
                fieldExists={doesFieldExist}
                inputRef={refs}
                onBlur={null}
                isDisabled={
                  !projectPermissions.canEditTypeWells ||
                  isImportedRatioArps ||
                  isImportedRateTemplate(product, productSegmentTemplate[product])
                }
                kind={kind}
              />
            );
          })}
        </ColumnContainer>
      </ProductColumnWrapper>
    );
  }

  const onSelectedProductTemplateChanged = ({
    product,
    templateName
  }: {
    product: string;
    templateName: string;
  }) => {
    // Reset undo stack when template changes since old undos are no longer valid
    if (fieldLevelUndoManager) {
      fieldLevelUndoManager.reset();
    }

    const templateIndex = typeWellTemplates.findIndex(
      (item) => item.name === templateName
    );
    if (templateIndex === -1) {
      return;
    }
    const toBeInitialSegment =
      templateName.includes("Ramp-Up") || templateName.includes("Constrained");
    const template = typeWellTemplates[templateIndex];

    const arpsGroupedByProduct = groupArpsSegmentsByProduct(arps.arps);
    const productArps = arpsGroupedByProduct[product];

    if (!productArps) {
      return;
    }

    const extendedProductArps = extendArpsWrapper({
      segmentTemplates: typeWellTemplates,
      arps: productArps,
      templateName,
      wellKey
    });

    if (extendedProductArps.length == 0) {
      return;
    }

    let updatedArps: ArpSegment[];
    try {
      updatedArps = updateArps({
        template,
        productArps: extendedProductArps,
        header: "Qi",
        val: toBeInitialSegment ? extendedProductArps[1].qi : extendedProductArps[0].qi,
        typeWellSettings,
        declineType,
        cumulativeData,
        setCalculatedFields,
        calculatedFields,
        primaryProduct: Object.keys(productSegmentTemplate)[0]
      });
    } catch (err) {
      setErrors((prevErrors: ErrorWarning[]) => [
        ...prevErrors.filter((error) => error.product !== product),
        ...Object.entries(err).map(([product, message]: [string, string]) => ({
          product,
          message: String(message)
        }))
      ]);
      return;
    }

    const newArps = [...arps.arps];
    const arpsIndex = arps.arps.findIndex((f) => f.product === product);
    if (arpsIndex >= 0) {
      const oldProductLength = newArps.filter((item) => item.product === product).length;
      newArps.splice(arpsIndex, oldProductLength, ...updatedArps);
    }
    const userArps = Object.assign({}, arps, {
      arps: newArps,
      reserveCategory: selectedTypeWellRescat
    });

    resetCalculatedFields();
    onChange && onChange(userArps);
  };

  const onSelectedOverallTemplateChanged = (templateName: string) => {
    // Reset undo stack when template changes since old undos are no longer valid
    if (fieldLevelUndoManager) {
      fieldLevelUndoManager.reset();
    }

    const templateIndex = typeWellTemplates.findIndex(
      (item) => item.name === templateName
    );
    if (templateIndex === -1) {
      return;
    }
    const toBeInitialSegment =
      templateName.includes("Ramp-Up") || templateName.includes("Constrained");
    const template = typeWellTemplates[templateIndex];

    // Filter out ratio decline segments - they use a fixed "2 Seg." template - and read-only rate columns
    const nonRatioSegments = arps.arps.filter(
      (seg) =>
        !isRatio(seg.product) &&
        !isImportedRateTemplate(seg.product, productSegmentTemplate[seg.product])
    );
    const arpsGroupedByProduct = groupArpsSegmentsByProduct(nonRatioSegments);
    const keys = Object.keys(arpsGroupedByProduct);
    let allArps = [];

    // Keep ratio segments (user declines and imported Arps) and read-only rate segments unchanged
    const unmanagedSegments = arps.arps.filter(
      (seg) =>
        isRatio(seg.product) ||
        isImportedRateTemplate(seg.product, productSegmentTemplate[seg.product])
    );

    try {
      for (const product of keys) {
        const productArps = arpsGroupedByProduct[product];

        const extendedProductArps = extendArpsWrapper({
          segmentTemplates: typeWellTemplates,
          arps: productArps,
          templateName,
          wellKey
        });

        if (extendedProductArps.length == 0) {
          continue;
        }
        const updatedArps: ArpSegment[] = updateArps({
          template,
          productArps: extendedProductArps,
          header: "Qi",
          val: toBeInitialSegment ? extendedProductArps[1].qi : extendedProductArps[0].qi,
          typeWellSettings,
          declineType,
          cumulativeData,
          setCalculatedFields,
          calculatedFields,
          primaryProduct: Object.keys(productSegmentTemplate)[0]
        });
        allArps = allArps.concat(updatedArps);
      }
    } catch (err) {
      setErrors([
        ...Object.entries(err).map(([product, message]: [string, string]) => ({
          product,
          message: String(message)
        }))
      ]);
      return;
    }
    if (allArps.length === 0) {
      setOverallSegmentTemplateAndUpdateHeader(template);
      return;
    }

    // Rebuild in the original product order so a primary unmanaged product stays first.
    const updatedByProduct = groupArpsSegmentsByProduct(allArps);
    const unmanagedByProduct = groupArpsSegmentsByProduct(unmanagedSegments);
    const seenProducts = new Set<string>();
    const orderedArps = arps.arps.flatMap((segment) => {
      if (seenProducts.has(segment.product)) return [];
      seenProducts.add(segment.product);
      return (
        updatedByProduct[segment.product] ?? unmanagedByProduct[segment.product] ?? []
      );
    });

    const userArps: ICheckedForecast = Object.assign({}, arps, {
      arps: orderedArps,
      reserveCategory: selectedTypeWellRescat
    });

    resetCalculatedFields();
    onChange && onChange(userArps);
    focusPrimaryProductQiField();
  };

  if (!typeWellTemplates?.length || !overallSegmentTemplate) {
    return <></>;
  }

  function getCurrentFieldValue(
    forecast: ICheckedForecast,
    product: string,
    header: string
  ) {
    if (!forecast || !forecast.arps) {
      return null;
    }

    const arpsIndex = forecast.arps.findIndex((f) => f.product === product);
    const constantIndex = (forecast.constants ?? []).findIndex(
      (f) => f.product.toUpperCase() === product.toUpperCase()
    );

    if (arpsIndex >= 0) {
      const arpSegment = forecast.arps[arpsIndex];
      // Map display header to actual field name before accessing the data
      const actualFieldName = mapHeaderToFieldName(header);
      if (arpSegment[actualFieldName] !== undefined) {
        return arpSegment[actualFieldName];
      }
    }

    if (constantIndex >= 0) {
      const constant = forecast.constants?.[constantIndex];
      if (constant) {
        // For declining ratios, map the specific headers to their fields
        if (header === ArpFields.Q0) {
          return constant.value;
        } else if (header === ArpFields.Qi) {
          // Return qi if it exists, otherwise fall back to value (for non-declining ratios)
          return constant.qi !== undefined ? constant.qi : constant.value;
        } else if (header === ArpFields.Duration) {
          return constant.duration;
        } else if (header === "value") {
          return constant.value;
        }
      }
    }

    return null;
  }

  // Map display headers to actual field names for data structure
  function mapHeaderToFieldName(header: string): string {
    const headerMap: { [key: string]: string } = {
      Qi: "qi",
      Qf: "qf",
      B: "b",
      "Max Rate": "maxRate",
      "Start Date": "startDate",
      "End Date": "endDate"
    };

    if (header.startsWith("Di (")) {
      return "di";
    }
    if (header.startsWith("Df (")) {
      return "df";
    }

    if (headerMap[header]) {
      return headerMap[header];
    }

    // Convert to camelCase for fallback
    return header.charAt(0).toLowerCase() + header.slice(1);
  }

  // Check if the given product matches the primary product from cumulativeData
  function isProductPrimary(product: string): boolean {
    if (!cumulativeData?.primaryProduct) {
      return false;
    }

    const primaryProd = cumulativeData.primaryProduct.toUpperCase();
    const currentProd = product.toUpperCase();

    // Handle exact matches and common variations
    if (currentProd === primaryProd) {
      return true;
    }

    // Handle special cases
    // "CONDENSATE" matches "COND" or "COND."
    if (
      (primaryProd === ProductNames.Condensate &&
        (currentProd === ProductNames.CondAbbreviated ||
          currentProd === ProductNames.CondAbbreviatedWithDot)) ||
      (currentProd === ProductNames.Condensate &&
        (primaryProd === ProductNames.CondAbbreviated ||
          primaryProd === ProductNames.CondAbbreviatedWithDot))
    ) {
      return true;
    }

    return false;
  }

  /**
   * Apply a single field through updateArps, then re-solve Di via ArpFields.EUR
   * so the drag does not move EUR.
   *
   * The EUR/b drags write the segment inline before calling ArpFields.EUR, but
   * that only works for fields whose whole effect is one property. Q0 and Qi are
   * not: on ramp-up templates Q0 also sets seg0.q0 and re-runs the harmonic ramp
   * solve, on constrained templates it recalculates di/df, and Qi sets the
   * ramp's qf as well as the decline's qi. Those live in updateArps'
   * per-template updaters, so run it first and hand the result to EUR.
   *
   * Returns false for missing/degenerate segments or a rejected solve, so
   * callers can fall back to the normal field validation path.
   */
  function applyFieldPreservingEUR(
    forecast: ICheckedForecast,
    productName: string,
    header: string,
    val: number,
    eur: number,
    history: boolean | AnchorDragHistory = false
  ): boolean {
    const productArps = forecast.arps.filter((seg) => seg.product === productName);
    if (productArps.length === 0) return false;
    if (isImportedRateTemplate(productName, productSegmentTemplate[productName])) {
      return false;
    }

    // The decision of whether EUR can be preserved lives in planAnchorDragApplication
    // so it is unit-testable; see anchorDragEur.test.ts.
    const plan = planAnchorDragApplication({
      header,
      value: val,
      targetEur: eur,
      productArps
    });
    if (plan.preserveEur === null) return false;

    // updateArps throws a product-keyed object on solve failure (unreachable
    // end date, or its catch-all) -- every other call site in this file
    // wraps it. This one is reached from the pendingDragQi/pendingDragPeak/
    // pendingDragB effects, which fire on every rAF tick of a drag, so an
    // uncaught throw here would tear down the editor mid-gesture. Fall back
    // to the field-only path (the same one used when EUR preservation isn't
    // possible) instead of letting it escape the effect.
    let updatedSegments: ArpSegment[];
    try {
      updatedSegments = updateArps({
        template: productSegmentTemplate[productName],
        productArps,
        header,
        val,
        typeWellSettings,
        declineType,
        cumulativeData,
        setCalculatedFields,
        calculatedFields,
        primaryProduct: Object.keys(productSegmentTemplate)[0]
      });
    } catch {
      // Rejected drag candidates are validation failures, not telemetry events.
      // The field-only fallback retains the editor's normal error feedback.
      return false;
    }
    // Replace each of this product's segments in place by its ordinal
    // position among same-product segments (matching how updatedSegments was
    // derived from productArps), rather than splicing a contiguous run --
    // arps is not guaranteed to keep one product's segments contiguous, and
    // splicing at [arpsIndex, productArps.length) would delete whatever
    // segments of an unrelated product happen to fall in that range.
    let productOrdinal = -1;
    const newList = forecast.arps.map((seg) => {
      if (seg.product !== productName) return seg;
      productOrdinal += 1;
      return updatedSegments[productOrdinal] ?? seg;
    });
    onArpsValueChange(
      { ...forecast, arps: newList },
      productName,
      ArpFields.EUR,
      eur,
      history
    );
    return true;
  }

  /**
   * Qi drag with EUR preservation, shared by the plain-Qi branch of a Qi-anchor
   * drag and the peak-anchor drag on ramp-up templates -- both target
   * ArpFields.Qi through the identical apply-then-fallback shape.
   */
  function applyQiPreservingEur(
    forecast: ICheckedForecast,
    productName: string,
    qi: number,
    eur: Nullable<number>,
    history: boolean | AnchorDragHistory
  ): void {
    if (
      eur != null &&
      applyFieldPreservingEUR(forecast, productName, ArpFields.Qi, qi, eur, history)
    ) {
      return;
    }
    onArpsValueChange(forecast, productName, ArpFields.Qi, qi, history);
  }

  function onArpsValueChange(
    forecast: ICheckedForecast,
    product: string,
    header: string,
    val,
    history: boolean | AnchorDragHistory = false
  ) {
    const isUndoAction = history !== false;
    try {
      if (!forecast || !forecast.arps) {
        return;
      }
      if (isImportedRateTemplate(product, productSegmentTemplate[product])) {
        return;
      }

      if (
        typeof history === "object" &&
        anchorCalculatedFields.current?.forecast !== history.originalForecast
      ) {
        anchorCalculatedFields.current = {
          forecast: history.originalForecast,
          fields: calculatedFields
        };
      }

      // Capture the current value before making changes for undo functionality
      const currentValue = getCurrentFieldValue(forecast, product, header);

      const productArps = forecast.arps.filter((f) => f.product === product);

      const arpsIndex = forecast.arps.findIndex((f) => f.product === product);
      const constantIndex = (forecast.constants ?? []).findIndex(
        (f) => f.product.toUpperCase() === product.toUpperCase()
      );
      if (arpsIndex < 0 && constantIndex < 0) {
        return;
      }
      let newList = [...forecast.arps];
      if (arpsIndex >= 0) {
        let updatedSegments: ArpSegment[];
        // Check if this is a ratio decline segment - handle it differently
        const isRatioDeclineSegment =
          productArps.length > 0 && productArps[0].isUserRatioDecline === true;

        if (isRatioDeclineSegment) {
          // For ratio decline segments, use the specialized update function
          // This prevents incorrect calculated field marking
          updatedSegments = updateRatioDeclineSegments({
            productArps,
            header,
            val
          });
        } else {
          // Normal segment update logic
          try {
            const productTemplate = productSegmentTemplate[product];
            updatedSegments = updateArps({
              template: productTemplate,
              productArps,
              header,
              val,
              typeWellSettings,
              declineType,
              cumulativeData,
              setCalculatedFields,
              calculatedFields,
              primaryProduct: Object.keys(productSegmentTemplate)[0]
            });
          } catch (err) {
            setErrors((prevErrors: ErrorWarning[]) => [
              ...prevErrors.filter((error) => error.product !== product),
              ...Object.entries(err).map(([product, message]: [string, string]) => ({
                product,
                message: String(message)
              }))
            ]);
            setWarnings((prevWarnings: ErrorWarning[]) => [
              ...prevWarnings.filter((warning) => warning.product !== product)
            ]);
            return;
          }
        }
        let productOrdinal = 0;
        newList = newList.flatMap((segment) => {
          if (segment.product !== product) return [segment];
          const ordinal = productOrdinal++;
          // Preserve interleaved products, but allow template edits (e.g. SAGD
          // tRamp) to add or remove segments of the edited product.
          if (ordinal === productArps.length - 1) return updatedSegments.slice(ordinal);
          return updatedSegments[ordinal] ? [updatedSegments[ordinal]] : [];
        });
      }
      const newConstants = [...(forecast.constants ?? [])];
      if (constantIndex >= 0) {
        const existingConstant = newConstants[constantIndex];

        // Build updated constant with appropriate field updates
        const updatedConstant: ForecastConstant = {
          product: existingConstant.product,
          uniqueId: existingConstant.uniqueId,
          value: existingConstant.value,
          unit: existingConstant.unit,
          qi: existingConstant.qi, // Preserve qi
          duration: existingConstant.duration // Preserve duration
        };

        // Update the appropriate field based on header
        if (isDecliningRatio(product) && kind === ArpsDesignerKind.TypeWell) {
          // Declining ratio products in TypeWell mode: handle Q0, Qi, Duration fields
          if (header === ArpFields.Q0) {
            updatedConstant.value = val;
          } else if (header === ArpFields.Qi) {
            updatedConstant.qi = parseNumber(val);
          } else if (header === ArpFields.Duration) {
            updatedConstant.duration = parseNumber(val);
          }
        } else {
          // Non-ratio products OR Forecasting mode: standard value update
          updatedConstant.value = val;
        }

        newConstants.splice(constantIndex, 1, updatedConstant);
      }

      // FEATURE: Handle ratio decline conversion between constant and segments
      // (only in TypeWell mode)
      let finalArps = newList;
      let finalConstants = newConstants;
      let didConversion = false; // Track if we did a conversion (for forcing template update)

      if (
        kind === ArpsDesignerKind.TypeWell &&
        isDecliningRatio(product) &&
        (header === ArpFields.Q0 ||
          header === ArpFields.Qi ||
          header === ArpFields.Duration)
      ) {
        // Create a temporary forecast object for conversion functions
        const tempForecast: ICheckedForecast = {
          ...forecast,
          arps: newList,
          constants: newConstants
        };

        // Get current display values for this ratio (includes updated qi/duration from above)
        const displayValues = getRatioDisplayValues(tempForecast, product);

        // Check if we should convert based on current values
        // Allow 0 as a valid value for Q0 and Qi (ratios can incline from 0 or decline to 0).
        // Cleared inputs default to 0, so Duration=0 acts as the "off switch" for declining mode.
        const hasQo = displayValues.qo !== undefined && displayValues.qo !== null;
        const hasQi = displayValues.qi !== undefined && displayValues.qi !== null;
        const hasDuration =
          displayValues.duration !== undefined &&
          displayValues.duration !== null &&
          displayValues.duration !== 0;

        // Get existing ratio segments
        const ratioSegments = newList.filter(
          (seg) => seg.product === product && seg.isUserRatioDecline
        );
        const isCurrentlySegments = ratioSegments.length > 0;

        // Conversion logic

        if (hasQo && hasQi && hasDuration && !isCurrentlySegments) {
          // Convert constant → segments
          const converted = convertConstantToSegments(
            tempForecast,
            product,
            displayValues.qo,
            displayValues.qi,
            displayValues.duration,
            kind,
            cumulativeData
          );
          finalArps = converted.arps;
          finalConstants = converted.constants;
          didConversion = true;
        } else if (isCurrentlySegments && !hasDuration) {
          // Convert segments → constant (user cleared Duration to 0)
          const qoToUse = displayValues.qo ?? 0;
          const converted = convertSegmentsToConstant(tempForecast, product, qoToUse);
          finalArps = converted.arps;
          finalConstants = converted.constants;
          didConversion = true;
        }
      }

      const userArps = {
        ...forecast,
        arps: finalArps,
        constants: finalConstants
      };

      // FEATURE: Cascade start date changes from primary product to all other products
      // (Only in Forecasting mode, not in TypeWellEditor)
      let finalUserArps = userArps;
      let shouldRecalculateAllProducts = false;
      let cascadedDateValue = null;

      if (
        kind === ArpsDesignerKind.Forecasting &&
        header === "Start Date" &&
        !isUndoAction &&
        isProductPrimary(product)
      ) {
        // Store the new date value for recalculation
        cascadedDateValue = val;
        // Cascade the date to all products
        finalUserArps = cascadeStartDateToAllProducts(val, userArps);
        shouldRecalculateAllProducts = true;
      }

      // FEATURE: Sync ratio decline segments with primary product end date
      // If this change affects the primary product (and it's not a ratio product change),
      // sync all ratio segments' dates to match the primary product's dates
      if (!isRatio(product) && isPrimaryProduct(product)) {
        // Get the primary product's first segment start date
        const primarySegments = finalUserArps.arps.filter(
          (seg) => seg.product === product && !seg.isUserRatioDecline
        );
        const primaryProductStartDate = primarySegments[0]?.startDate
          ? new Date(primarySegments[0].startDate)
          : null;

        // Get the primary product's last valid segment end date
        const primaryProductEndDate = getPrimaryProductLastSegmentEndDate({
          segments: finalUserArps.arps,
          kind,
          cumulativeData
        });

        if (primaryProductStartDate && primaryProductEndDate) {
          // Sync all ratio segments to match these dates
          finalUserArps = syncRatioSegmentsWithPrimaryProduct(
            finalUserArps,
            primaryProductStartDate,
            primaryProductEndDate
          );
        }
      }

      // Add the field change to the undo stack BEFORE any early returns (only if this is not an undo operation)
      if (fieldLevelUndoManager && currentValue !== val && !isUndoAction) {
        const fieldPath = `${product}.${header}`;
        // Capture the current calculatedFields for this product before the change
        const prevCalculatedFields = calculatedFields[product]
          ? [...calculatedFields[product]]
          : [ArpFields.EUR, ArpFields.TotalEUR];

        // For cascaded start date changes, we need to store all affected products' dates
        const affectedProductDates: { [product: string]: string } = {};
        if (
          kind === ArpsDesignerKind.Forecasting &&
          header === "Start Date" &&
          isProductPrimary(product)
        ) {
          // Store the start dates of all products before the cascade
          forecast.arps.forEach((segment, index) => {
            if (isFirstProductSegment(forecast.arps, segment, index)) {
              affectedProductDates[segment.product] = segment.startDate;
            }
          });
        }

        // Create a command that stores the values and calculatedFields
        const command = createFieldCommand(
          fieldPath,
          currentValue,
          val,
          () => {
            // Execute: We don't need to do anything here since the change was already applied
          },
          () => {
            // Undo: Apply the old value and restore calculatedFields for this product
            const currentForecast = arps;
            if (currentForecast && currentForecast.arps) {
              let updatedForecast = {
                ...currentForecast,
                arps: [...(currentForecast.arps ?? [])],
                constants: [...(currentForecast.constants ?? [])]
              };
              const arpsIndex = updatedForecast.arps.findIndex(
                (f) => f.product === product
              );
              const constantIndex = updatedForecast.constants.findIndex(
                (f) => f.product.toUpperCase() === product.toUpperCase()
              );
              if (arpsIndex >= 0) {
                const actualFieldName = mapHeaderToFieldName(header);
                updatedForecast.arps[arpsIndex] = {
                  ...updatedForecast.arps[arpsIndex],
                  [actualFieldName]: currentValue
                };
              } else if (constantIndex >= 0 && header === "value") {
                updatedForecast.constants[constantIndex] = {
                  ...updatedForecast.constants[constantIndex],
                  value: currentValue
                };
              }

              // If this was a cascaded start date change, restore all products' dates
              if (
                kind === ArpsDesignerKind.Forecasting &&
                header === "Start Date" &&
                isProductPrimary(product) &&
                Object.keys(affectedProductDates).length > 0
              ) {
                const restoredArps = updatedForecast.arps.map((segment, index) => {
                  if (
                    isFirstProductSegment(updatedForecast.arps, segment, index) &&
                    affectedProductDates[segment.product]
                  ) {
                    return {
                      ...segment,
                      startDate: affectedProductDates[segment.product]
                    };
                  }
                  return segment;
                });
                updatedForecast = {
                  ...updatedForecast,
                  arps: restoredArps
                };
              }

              // Restore calculatedFields for this product
              setCalculatedFields((prev) => ({
                ...prev,
                [product]: prevCalculatedFields
              }));
              onChange && onChange(updatedForecast);
            }
          }
        );
        // Add command to history without executing (since we already executed the change above)
        fieldLevelUndoManager.addCommand(command);
      }

      // For Q* fields, don't update productSegmentTemplate during normal user input
      // to prevent re-rendering and focus loss. Only update for actual backend (arps-wasm) corrections.
      // EXCEPTION: If we just did a conversion (constant→segments or segments→constant),
      // we MUST update the template to rebuild the proper structure
      if (!header.startsWith("Q") || didConversion) {
        // For non-Q fields (or after conversion), always update the template data
        const updatedProductTemplates: ProductSegmentTemplate =
          updateProductTemplatesAndData({
            arps: finalUserArps,
            typeWellTemplates,
            arpsWasm,
            kind,
            setTemplateError,
            cumulativeData,
            declineType,
            typeWellSettings,
            updateEUR,
            wellKey
          });
        const sortedProductTemplates: ProductSegmentTemplate = getSortedProducts({
          updatedProductTemplates,
          kind,
          cumulativeData
        });
        setProductSegmentTemplate(sortedProductTemplates);

        // If we cascaded start dates, we need to recalculate ALL products
        if (shouldRecalculateAllProducts) {
          // Rebuild the arps array by recalculating each product with the new start date
          const recalculatedArps = [];

          Object.keys(sortedProductTemplates).forEach((prod) => {
            const productTemplate = sortedProductTemplates[prod];
            // IMPORTANT: Use the ORIGINAL segments (userArps) before cascade, not the cascaded ones (finalUserArps)
            // This ensures updateArps can properly calculate durations like tFlat and tRamp
            // before applying the new start date
            const productSegments = userArps.arps.filter((s) => s.product === prod);

            // Read-only: carry segments through rather than relying on updateArps to throw.
            if (isImportedRateTemplate(prod, productTemplate)) {
              recalculatedArps.push(...productSegments);
              return;
            }

            try {
              // Recalculate each product using the updateArps logic
              const recalculatedSegments = updateArps({
                template: productTemplate,
                productArps: productSegments,
                header: "Start Date",
                val: cascadedDateValue, // Use the cascaded date value from the primary product
                typeWellSettings,
                declineType,
                cumulativeData,
                setCalculatedFields,
                calculatedFields,
                primaryProduct: Object.keys(productSegmentTemplate)[0]
              });

              recalculatedArps.push(...recalculatedSegments);
            } catch (err) {
              // If recalculation fails for a product, use the original segments
              recalculatedArps.push(...productSegments);
            }
          });

          // Update finalUserArps with the recalculated segments
          finalUserArps = {
            ...finalUserArps,
            arps: recalculatedArps
          };

          // Validate all products after recalculation
          const { hasErrors, hasWarnings } = validateSegmentsAndUpdateStatesWrapper({
            arps: recalculatedArps,
            productSegmentTemplate: sortedProductTemplates
          });

          // Call onChange with validation results
          onChange && onChange(finalUserArps, hasErrors, hasWarnings);
          return; // Exit early since we've already called onChange with validation
        }
      }

      if (typeof history === "object") {
        const { originalForecast, isPreview } = history;
        // One anchor gesture changes coupled fields across segments. Restore the
        // mouse-down snapshot instead of trying to invert its final EUR solve.
        const applySnapshot = (snapshot: ICheckedForecast) =>
          onChange?.(snapshot, undefined, undefined, { recordHistory: false });
        if (originalForecast && fieldLevelUndoManager) {
          const previousCalculatedFields = anchorCalculatedFields.current?.fields;
          const before = {
            ...forecast,
            arps: originalForecast.arps,
            constants: originalForecast.constants
          };
          const command = createFieldCommand(
            `${product}.${header}`,
            currentValue,
            val,
            () => applySnapshot(finalUserArps),
            () => {
              if (previousCalculatedFields) setCalculatedFields(previousCalculatedFields);
              applySnapshot(before);
            }
          );
          pendingAnchorCommand.current = () => fieldLevelUndoManager.addCommand(command);
        }
        applySnapshot(finalUserArps);
        if (!isPreview) finalizeAnchorHistory();
      } else {
        onChange && onChange(finalUserArps);
      }
    } catch (err) {
      setErrors([{ product, message: "An error occurred." }]);
    }
  }

  return (
    <RootContainer data-testid="type-well-input-table">
      <ErrorBoundary>
        <TemplateSelectorWrapper data-testid="template-selector-wrapper">
          {mode === TypeWellInputTableMode.Product ? (
            <RowHeading className="overallTemplateLabel">Overall Template</RowHeading>
          ) : (
            <SwitchToProductModeWrapper>
              <Button icon={<ArrowBack />} type="primary" onClick={switchToProductMode}>
                Back
              </Button>
            </SwitchToProductModeWrapper>
          )}
          {templateError && (
            <Popover content={<div>{templateError}</div>}>
              <ErrorWrapper>
                <ErrorOutlineOutlined />
              </ErrorWrapper>
            </Popover>
          )}
          {mode === TypeWellInputTableMode.Product ? (
            <Select
              ref={(node) => {
                if (node) {
                  const element = document.querySelector(
                    ".overall-template-select .ant-select-selector"
                  );
                  dropdownRef.current = element as HTMLElement;
                }
              }}
              className="overall-template-select"
              value={getOverallTemplateName({
                overallSegmentTemplate,
                productSegmentTemplate
              })}
              onChange={onSelectedOverallTemplateChanged}
              onSelect={(value) => {
                const currentValue = getOverallTemplateName({
                  overallSegmentTemplate,
                  productSegmentTemplate
                });
                if (value === currentValue) {
                  dropdownRef.current?.blur();
                  focusPrimaryProductQiField();
                }
              }}
              options={typeWellTemplates.map((item) => ({
                label: item.name,
                value: item.name
              }))}
              disabled={hasSource || !projectPermissions.canEditTypeWells}
            />
          ) : (
            <strong>
              {segmentDetails.productCols.length > 0
                ? segmentDetails?.productCols[0]
                : null}
              {" Segments"}
            </strong>
          )}

          {mode === TypeWellInputTableMode.Product ? (
            <Tooltip
              placement="top"
              title={
                Object.values(productSegmentTemplate).every(
                  (template) => template.productCols.length === 0
                )
                  ? "Select a product first"
                  : ""
              }>
              <Select
                className="rescat-select"
                placeholder="Rescat"
                value={selectedTypeWellRescat || undefined}
                onChange={(e) => {
                  fsSetSelectedTypeWell({
                    ...selectedTypeWell,
                    reserveCategory: e
                  });
                  setIsTypeWellSavable(true);
                }}
                options={[
                  ...RESERVE_CATEGORIES.map((rc) => ({
                    label: rc,
                    value: rc
                  }))
                ]}
                disabled={
                  hasSource ||
                  Object.keys(productSegmentTemplate).length === 0 ||
                  Object.values(productSegmentTemplate).some(
                    (template) => template.productCols.length === 0
                  ) ||
                  !projectPermissions.canEditTypeWells
                }
              />
            </Tooltip>
          ) : (
            <></>
          )}

          {mode === TypeWellInputTableMode.Product &&
            kind === ArpsDesignerKind.Forecasting && (
              <ButtonIconCentered
                disabled={
                  hasSource ||
                  Object.keys(productSegmentTemplate).length === 0 ||
                  Object.values(productSegmentTemplate).some(
                    (template) => template.productCols.length === 0
                  ) ||
                  !projectPermissions.canEditTypeWells
                }
                icon={<Icon path={mdiClock} size={1.0} />}
                onClick={() => setOnTime({ ...onTime, open: true })}>
                On Time
              </ButtonIconCentered>
            )}
        </TemplateSelectorWrapper>
        <Wrapper>
          <TableContainer className="typeWellInputTableContainer">
            <ProductColumnContainer>
              <ProductColumnWrapper>
                <ColumnContainer>
                  {/*Header Column*/}
                  {(mode === TypeWellInputTableMode.Segment
                    ? segmentDetails.rowHeaders
                    : uniqueProductFields.rowHeaders ?? []
                  ).map((header, idx) => {
                    const displayHeader = (
                      mode === TypeWellInputTableMode.Segment
                        ? segmentDetails.displayHeaders
                        : uniqueProductFields.displayHeaders
                    )[idx]?.split(".");
                    const headerUnit = header.split(" ");
                    const tooltipText = getHeaderTooltip(header);

                    // Render header with multiple subscripts support
                    const renderHeaderText = () => {
                      if (displayHeader.length === 1) {
                        // No subscripts
                        return displayHeader[0];
                      } else if (displayHeader.length === 2) {
                        // Single subscript (e.g., "Q.i" -> "Q" with subscript "i")
                        return (
                          <>
                            {displayHeader[0]}{" "}
                            <sub>
                              {displayHeader[1]}{" "}
                              {headerUnit.length > 1 ? headerUnit[1] : null}
                            </sub>
                          </>
                        );
                      } else if (displayHeader.length === 3) {
                        // Double subscript (e.g., "Q.f.Cutoff" -> "Q" with subscript "f" then space then "Cutoff")
                        return (
                          <>
                            {displayHeader[0]}
                            <sub>{displayHeader[1]}</sub> <sub>{displayHeader[2]}</sub>
                          </>
                        );
                      } else {
                        // Fallback for unexpected formats
                        return displayHeader.join(".");
                      }
                    };

                    const headerContent = renderHeaderText();

                    return (
                      <RowHeading key={header} className="header">
                        {tooltipText ? (
                          <Tooltip title={tooltipText}>{headerContent}</Tooltip>
                        ) : (
                          headerContent
                        )}
                      </RowHeading>
                    );
                  })}
                </ColumnContainer>
              </ProductColumnWrapper>

              {/*Product Columns*/}
              {(mode === TypeWellInputTableMode.Segment
                ? Array.from(new Set(segmentDetails?.productCols ?? []))
                : Object.keys(productSegmentTemplate)
              ).map((product: string, productIndex: number) => {
                let productDataArray;

                if (mode === TypeWellInputTableMode.Segment) {
                  productDataArray = segmentDetails?.productDisplayData || [];
                  product = segmentDetails.productCols[0];
                } else {
                  productDataArray =
                    productSegmentTemplate[product]?.productDisplayData || [];
                }

                return productDataArray.map((_productData, dataIndex) => {
                  return getProductInputs({
                    product,
                    refs: inputRefs,
                    productIndex,
                    segmentIndex: dataIndex,
                    forecastConstant: arps?.constants?.some((p) => p.product === product),
                    isRatio: isRatio(product),
                    isSync: hasSource
                  });
                });
              })}
              {mode === TypeWellInputTableMode.Product &&
                !hasSource &&
                !templateError && (
                  <NewProductWrapper>
                    <Dropdown
                      disabled={!projectPermissions.canEditTypeWells || hasForecastData}
                      menu={{ items: products, onClick: onAddNewProduct }}
                      // Named so the guided walkthrough can wait for this menu
                      // rather than for the edit it eventually causes.
                      overlayClassName="add-tw-product-dropdown"
                      trigger={["click"]}>
                      <Button
                        data-testid="add-new-tw-product-button"
                        type="primary"
                        icon={<Add />}>
                        Product <ChevronDown />
                      </Button>
                    </Dropdown>
                  </NewProductWrapper>
                )}
            </ProductColumnContainer>
          </TableContainer>
        </Wrapper>
        <OnTimeEditorModal
          {...onTime}
          onTime={arps?.onTimeArray ?? []}
          startDate={
            arps?.arps && arps?.arps.length > 0 && arps?.arps[0].startDate.length > 12
              ? new Date(arps.arps[0].startDate).getTime()
              : Date.now()
          }
          onCancel={() => setOnTime({ ...onTime, open: false })}
          onOk={(onTimeArray) => {
            onOnTimeChange && onTimeArray && onOnTimeChange(onTimeArray);
            setOnTime({ ...onTime, open: false });
          }}
        />
      </ErrorBoundary>
    </RootContainer>
  );
}
